// utils/taxId.ts
//
// Universal Tax ID Inspector & State/Province Dictionaries for 240+ Countries
// 100% feature-matched between App and Web.

// ── 1. Regional State & Province Dictionaries ────────────────────────────────

export const GST_STATE_CODES: Record<string, string> = {
  '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
  '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan',
  '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
  '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram', '16': 'Tripura',
  '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal', '20': 'Jharkhand',
  '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '26': 'Dadra and Nagar Haveli and Daman and Diu', '27': 'Maharashtra', '28': 'Andhra Pradesh (Old)',
  '29': 'Karnataka', '30': 'Goa', '31': 'Lakshadweep', '32': 'Kerala',
  '33': 'Tamil Nadu', '34': 'Puducherry', '35': 'Andaman and Nicobar Islands',
  '36': 'Telangana', '37': 'Andhra Pradesh', '38': 'Ladakh', '97': 'Other Territory',
};

export const US_STATES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia', PR: 'Puerto Rico', VI: 'Virgin Islands', GU: 'Guam',
};

export const CANADA_PROVINCES: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

export const AUSTRALIA_STATES: Record<string, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
  NT: 'Northern Territory',
};

export const BRAZIL_STATES: Record<string, string> = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará',
  DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão',
  MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais', PA: 'Pará',
  PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí', RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul', RO: 'Rondônia', RR: 'Roraima',
  SC: 'Santa Catarina', SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins',
};

export const UAE_EMIRATES: Record<string, string> = {
  AUH: 'Abu Dhabi',
  DXB: 'Dubai',
  SHJ: 'Sharjah',
  AJM: 'Ajman',
  UAQ: 'Umm Al Quwain',
  RAK: 'Ras Al Khaimah',
  FUJ: 'Fujairah',
};

export const ARGENTINA_PROVINCES: Record<string, string> = {
  CABA: 'Ciudad Autónoma de Buenos Aires',
  BA: 'Buenos Aires Provincia',
  CBA: 'Córdoba',
  SF: 'Santa Fe',
  MZA: 'Mendoza',
  ER: 'Entre Ríos',
  TUC: 'Tucumán',
  SAL: 'Salta',
  CHA: 'Chaco',
  CRRI: 'Corrientes',
  MIS: 'Misiones',
  SGO: 'Santiago del Estero',
  SJ: 'San Juan',
  JUJ: 'Jujuy',
  RN: 'Río Negro',
  NEU: 'Neuquén',
  FOR: 'Formosa',
  CHU: 'Chubut',
  SL: 'San Luis',
  CAT: 'Catamarca',
  LR: 'La Rioja',
  LP: 'La Pampa',
  SC: 'Santa Cruz',
  TDF: 'Tierra del Fuego',
};

export const PAKISTAN_PROVINCES: Record<string, string> = {
  SRB: 'Sindh (SRB - 13%)',
  PRA: 'Punjab (PRA - 16%)',
  KPRA: 'Khyber Pakhtunkhwa (KPRA - 15%)',
  BRA: 'Balochistan (BRA - 15%)',
  ICT: 'Islamabad Capital (ICT - 15%)',
  SD: 'Sindh (SRB - 13%)',
  PB: 'Punjab (PRA - 16%)',
  KP: 'Khyber Pakhtunkhwa (KPRA - 15%)',
  BA: 'Balochistan (BRA - 15%)',
  IS: 'Islamabad Capital (ICT - 15%)',
};

export const SPAIN_SPECIAL_REGIONS: Record<string, string> = {
  GC: 'Canary Islands (Las Palmas - IGIC 7%)',
  TF: 'Canary Islands (Santa Cruz de Tenerife - IGIC 7%)',
  CAN: 'Canary Islands (IGIC 7%)',
  CE: 'Ceuta (IPSI)',
  ML: 'Melilla (IPSI)',
  MD: 'Madrid (IVA 21%)',
  CT: 'Catalonia (IVA 21%)',
  AN: 'Andalusia (IVA 21%)',
  VC: 'Valencia (IVA 21%)',
};

export const PORTUGAL_REGIONS: Record<string, string> = {
  AC: 'Azores (Reduced VAT 16%)',
  AZO: 'Azores (Reduced VAT 16%)',
  MD: 'Madeira (Reduced VAT 22%)',
  MAD: 'Madeira (Reduced VAT 22%)',
  CT: 'Continental Portugal (VAT 23%)',
};

export const SWISS_CANTONS: Record<string, string> = {
  ZH: 'Zürich', BE: 'Bern', LU: 'Luzern', UR: 'Uri', SZ: 'Schwyz', OW: 'Obwalden',
  NW: 'Nidwalden', GL: 'Glarus', ZG: 'Zug', FR: 'Fribourg', SO: 'Solothurn',
  BS: 'Basel-Stadt', BL: 'Basel-Landschaft', SH: 'Schaffhausen', AR: 'Appenzell Ausserrhoden',
  AI: 'Appenzell Innerrhoden', SG: 'St. Gallen', GR: 'Graubünden', AG: 'Aargau',
  TG: 'Thurgau', TI: 'Ticino', VD: 'Vaud', VS: 'Valais', NE: 'Neuchâtel',
  GE: 'Genève', JU: 'Jura',
};

export const NIGERIA_STATES: Record<string, string> = {
  LA: 'Lagos State (5% Consumption Tax)',
  LAG: 'Lagos State (5% Consumption Tax)',
  FC: 'Abuja Federal Capital Territory',
  FCT: 'Abuja Federal Capital Territory',
  RI: 'Rivers State',
  KN: 'Kano State',
  OG: 'Ogun State',
  ED: 'Edo State',
  OY: 'Oyo State',
  AN: 'Anambra State',
  KD: 'Kaduna State',
};

export const MEXICO_REGIONS: Record<string, string> = {
  BC: 'Baja California (Border 8% VAT)',
  BCS: 'Baja California Sur (Border 8% VAT)',
  SON: 'Sonora (Border 8% VAT Zone)',
  CHIH: 'Chihuhua (Border 8% VAT Zone)',
  COAH: 'Coahuila (Border 8% VAT Zone)',
  NL: 'Nuevo León (Border Zone)',
  TAM: 'Tamaulipas (Border 8% VAT Zone)',
  QROO: 'Quintana Roo (Chetumal Border Zone)',
  CDMX: 'Ciudad de México (Mainland 16% IVA)',
  JAL: 'Jalisco (Mainland 16% IVA)',
  NLE: 'Nuevo León (Mainland 16% IVA)',
};

export const CHINA_PROVINCES: Record<string, string> = {
  '11': 'Beijing', '12': 'Tianjin', '13': 'Hebei', '14': 'Shanxi', '15': 'Inner Mongolia',
  '21': 'Liaoning', '22': 'Jilin', '23': 'Heilongjiang',
  '31': 'Shanghai', '32': 'Jiangsu', '33': 'Zhejiang', '34': 'Anhui', '35': 'Fujian', '36': 'Jiangxi', '37': 'Shandong',
  '41': 'Henan', '42': 'Hubei', '43': 'Hunan', '44': 'Guangdong (Shenzhen/Guangzhou)', '45': 'Guangxi', '46': 'Hainan (Free Trade Port 15%)',
  '50': 'Chongqing', '51': 'Sichuan', '52': 'Guizhou', '53': 'Yunnan', '54': 'Tibet',
  '61': 'Shaanxi', '62': 'Gansu', '63': 'Qinghai', '64': 'Ningxia', '65': 'Xinjiang',
};

export const ISRAEL_REGIONS: Record<string, string> = {
  EILAT: 'Eilat (0% VAT Free Trade Zone)',
  ETH: 'Eilat (0% VAT Free Trade Zone)',
};

export const DENMARK_REGIONS: Record<string, string> = {
  GL: 'Greenland (0% VAT Exempt Territory)',
  FO: 'Faroe Islands (Independent TAKS / MVG)',
};

export const BOSNIA_ENTITIES: Record<string, string> = {
  FBIH: 'Federation of Bosnia and Herzegovina (PUFBiH)',
  RS: 'Republika Srpska (PURS)',
  BD: 'Brčko District (PUD)',
};

export const AUSTRIA_SPECIAL_REGIONS: Record<string, string> = {
  JUNG: 'Jungholz (German VAT Enclave)',
  KLEIN: 'Mittelberg / Kleinwalsertal (German VAT Enclave)',
};

export const LATAM_FREE_ZONES: Record<string, string> = {
  ZLC: 'Panama (Zona Libre de Colón - 0% ITBMS)',
  PACIFICO: 'Panama (Panama Pacífico 0% ITBMS)',
  RZF: 'Costa Rica (Régimen Zona Franca - 0% IVA)',
  ZF: 'Dominican Rep / Uruguay (Zona Franca - 0% VAT)',
  ZONAMERICA: 'Uruguay (Zonamerica 0% IVA)',
};

export const UK_DEVOLVED_REGIONS: Record<string, string> = {
  SCOT: 'Scotland (Revenue Scotland / Devolved)',
  WRA: 'Wales (Welsh Revenue Authority)',
  IM: 'Isle of Man (Shared UK VAT Territory)',
};

export const FRANCE_DOM_REGIONS: Record<string, string> = {
  '971': 'Guadeloupe (Reduced VAT 8.5%)',
  '972': 'Martinique (Reduced VAT 8.5%)',
  '973': 'Guyane (0% VAT - Exempt)',
  '974': 'La Réunion (Reduced VAT 8.5%)',
  '976': 'Mayotte (0% VAT - Exempt)',
  '2A': 'Corse-du-Sud (Corsica Special VAT)',
  '2B': 'Haute-Corse (Corsica Special VAT)',
};

export const GREECE_ISLANDS: Record<string, string> = {
  LES: 'Lesbos (Aegean 17% Reduced VAT)',
  CHI: 'Chios (Aegean 17% Reduced VAT)',
  SAM: 'Samos (Aegean 17% Reduced VAT)',
  LER: 'Leros (Aegean 17% Reduced VAT)',
  KOS: 'Kos (Aegean 17% Reduced VAT)',
};

export const COLOMBIA_DEPARTMENTS: Record<string, string> = {
  BOG: 'Bogotá D.C. (ICA Tax)',
  ANT: 'Antioquia / Medellín (ICA Tax)',
  VAL: 'Valle del Cauca / Cali (ICA Tax)',
  ATL: 'Atlántico / Barranquilla (ICA Tax)',
  SAN: 'Santander / Bucaramanga',
  SAI: 'San Andrés y Providencia (0% IVA Free Port)',
};

export const INDONESIA_PROVINCES: Record<string, string> = {
  JKT: 'DKI Jakarta (PBJT 10% Local Tax)',
  BALI: 'Bali (Badung/Denpasar PBJT 10%)',
  JABAR: 'Jawa Barat / Bandung',
  JATIM: 'Jawa Timur / Surabaya',
  JATENG: 'Jawa Tengah / Semarang',
};

export const PERU_REGIONS: Record<string, string> = {
  LOR: 'Loreto (Amazonía 0% IGV Exonerado)',
  UCY: 'Ucayali (Amazonía 0% IGV Exonerado)',
  SM: 'San Martín (Amazonía 0% IGV Exonerado)',
  AMA: 'Amazonas (Amazonía 0% IGV Exonerado)',
  MDD: 'Madre de Dios (Amazonía 0% IGV Exonerado)',
  LIM: 'Lima / Callao (Mainland 18% IGV)',
  CUS: 'Cusco (Mainland 18% IGV)',
  ARE: 'Arequipa (Mainland 18% IGV)',
};

export const MALAYSIA_REGIONS: Record<string, string> = {
  LBN: 'Labuan (0% Duty-Free Island)',
  LGK: 'Langkawi (0% Duty-Free Island)',
  TIO: 'Tioman (0% Duty-Free Island)',
  PKG: 'Pangkor (0% Duty-Free Island)',
  SBH: 'Sabah (State Sales Tax)',
  SWK: 'Sarawak (State Sales Tax)',
  KL: 'Kuala Lumpur (Federal SST)',
  SGR: 'Selangor (Federal SST)',
};

export const PHILIPPINES_REGIONS: Record<string, string> = {
  PEZA: 'PEZA Registered Zone (0% VAT Zero-Rated)',
  SBFZ: 'Subic Bay Freeport (0% VAT / 5% GIT)',
  CFZ: 'Clark Freeport Zone (0% VAT / 5% GIT)',
  NCR: 'National Capital Region / Manila (12% VAT)',
  CEB: 'Cebu (12% VAT)',
};

export const CHILE_REGIONS: Record<string, string> = {
  ZOFRI: 'Iquique / ZOFRI (0% IVA Zona Franca)',
  PA: 'Punta Arenas (0% IVA Zona Franca)',
  STGO: 'Santiago Metropolitana (19% IVA)',
  VALP: 'Valparaíso (19% IVA)',
};

export const NETHERLANDS_REGIONS: Record<string, string> = {
  BON: 'Bonaire (Caribbean ABB 8%)',
  EUS: 'Sint Eustatius (Caribbean ABB 6%)',
  SAB: 'Saba (Caribbean ABB 6%)',
  AMS: 'Mainland Netherlands (21% BTW)',
};

export const ITALY_SPECIAL_REGIONS: Record<string, string> = {
  LIV: 'Livigno (0% VAT / Outside EU VAT)',
  SO: 'Livigno (Sondrio - 0% VAT)',
  CAM: 'Campione d’Italia (Special Customs Zone)',
  CO: 'Campione d’Italia (Como)',
  BZ: 'Bolzano / South Tyrol (Autonomous)',
  TN: 'Trento (Autonomous)',
  AO: 'Valle d’Aosta (Autonomous)',
  SIC: 'Sicilia (Autonomous)',
  SAR: 'Sardegna (Autonomous)',
};

export const FINLAND_REGIONS: Record<string, string> = {
  AX: 'Åland Islands (0% EU VAT / Special Customs Zone)',
  ALA: 'Åland Islands (Autonomous)',
};

export const NORWAY_REGIONS: Record<string, string> = {
  SJM: 'Svalbard & Jan Mayen (0% MVA Exempt)',
  SV: 'Svalbard (0% MVA Exempt)',
};

export const GERMANY_STATES: Record<string, string> = {
  BW: 'Baden-Württemberg', BY: 'Bavaria', BE: 'Berlin', BB: 'Brandenburg',
  HB: 'Bremen', HH: 'Hamburg', HE: 'Hesse', MV: 'Mecklenburg-Vorpommern',
  NI: 'Lower Saxony', NW: 'North Rhine-Westphalia', RP: 'Rhineland-Palatinate',
  SL: 'Saarland', SN: 'Saxony', ST: 'Saxony-Anhalt', SH: 'Schleswig-Holstein', TH: 'Thuringia',
  BUES: 'Büsingen am Hochrhein (Swiss Customs Enclave)',
  HELG: 'Heligoland (0% MwSt Duty-Free)',
};

export const RUSSIA_REGIONS: Record<string, string> = {
  '77': 'Moscow (City)', '78': 'Saint Petersburg', '50': 'Moscow Oblast',
  '23': 'Krasnodar Krai', '66': 'Sverdlovsk Oblast', '16': 'Republic of Tatarstan',
  '54': 'Novosibirsk Oblast', '52': 'Nizhny Novgorod', '63': 'Samara Oblast',
  '02': 'Republic of Bashkortostan', '61': 'Rostov Oblast', '74': 'Chelyabinsk Oblast',
};

export const ECUADOR_PROVINCES: Record<string, string> = {
  '01': 'Azuay', '02': 'Bolívar', '03': 'Cañar', '04': 'Carchi',
  '05': 'Cotopaxi', '06': 'Chimborazo', '07': 'El Oro', '08': 'Esmeraldas',
  '09': 'Guayas (Guayaquil)', '10': 'Imbabura', '11': 'Loja', '12': 'Los Ríos',
  '13': 'Manabí', '14': 'Morona Santiago', '15': 'Napo', '16': 'Pastaza',
  '17': 'Pichincha (Quito)', '18': 'Tungurahua', '19': 'Zamora Chinchipe',
  '20': 'Galápagos (Special Regime)', '21': 'Sucumbíos', '22': 'Orellana',
  '23': 'Santo Domingo', '24': 'Santa Elena',
};

export const VIETNAM_PROVINCES: Record<string, string> = {
  '01': 'Hanoi', '02': 'Hai Phong', '03': 'Ho Chi Minh City', '04': 'Da Nang',
  '05': 'Ha Giang', '36': 'Dong Nai', '37': 'Binh Duong', '43': 'Ba Ria - Vung Tau',
};

export const KOREA_DISTRICTS: Record<string, string> = {
  '101': 'Seoul (Jongno)',
  '104': 'Seoul (Gangnam)',
  '114': 'Seoul (Yeongdeungpo)',
  '211': 'Incheon',
  '601': 'Busan',
  '616': 'Jeju (Special Free International City)',
};

// ── 2. Master Global Registry (240+ Countries & Territories) ─────────────────

export interface GlobalCountryMeta {
  name: string;
  flag: string;
  taxLabel: string;
  expectedLength?: number;
}

export const GLOBAL_COUNTRIES: Record<string, GlobalCountryMeta> = {
  // ── North America & Caribbean
  US: { name: 'United States', flag: '🇺🇸', taxLabel: 'EIN / Tax ID', expectedLength: 9 },
  CA: { name: 'Canada', flag: '🇨🇦', taxLabel: 'GST/HST No.', expectedLength: 9 },
  MX: { name: 'Mexico', flag: '🇲🇽', taxLabel: 'RFC', expectedLength: 12 },
  CR: { name: 'Costa Rica', flag: '🇨🇷', taxLabel: 'Cédula Jurídica / NIF', expectedLength: 10 },
  PA: { name: 'Panama', flag: '🇵🇦', taxLabel: 'RUC', expectedLength: 11 },
  GT: { name: 'Guatemala', flag: '🇬🇹', taxLabel: 'NIT', expectedLength: 9 },
  HN: { name: 'Honduras', flag: '🇭🇳', taxLabel: 'RTN', expectedLength: 14 },
  SV: { name: 'El Salvador', flag: '🇸🇻', taxLabel: 'NIT', expectedLength: 13 },
  NI: { name: 'Nicaragua', flag: '🇳🇮', taxLabel: 'RUC', expectedLength: 14 },
  DO: { name: 'Dominican Republic', flag: '🇩🇴', taxLabel: 'RNC', expectedLength: 9 },
  JM: { name: 'Jamaica', flag: '🇯🇲', taxLabel: 'TRN', expectedLength: 9 },
  TT: { name: 'Trinidad and Tobago', flag: '🇹🇹', taxLabel: 'BIR No.', expectedLength: 10 },
  BS: { name: 'Bahamas', flag: '🇧🇸', taxLabel: 'TIN', expectedLength: 10 },
  BB: { name: 'Barbados', flag: '🇧🇧', taxLabel: 'TAMIS TIN', expectedLength: 13 },
  BZ: { name: 'Belize', flag: '🇧🇿', taxLabel: 'TIN', expectedLength: 9 },
  CU: { name: 'Cuba', flag: '🇨🇺', taxLabel: 'NIT', expectedLength: 11 },
  HT: { name: 'Haiti', flag: '🇭🇹', taxLabel: 'NIF', expectedLength: 10 },
  AG: { name: 'Antigua and Barbuda', flag: '🇦🇬', taxLabel: 'TIN', expectedLength: 10 },
  DM: { name: 'Dominica', flag: '🇩🇲', taxLabel: 'TIN', expectedLength: 10 },
  GD: { name: 'Grenada', flag: '🇬🇩', taxLabel: 'TIN', expectedLength: 10 },
  KN: { name: 'Saint Kitts and Nevis', flag: '🇰🇳', taxLabel: 'TIN', expectedLength: 10 },
  LC: { name: 'Saint Lucia', flag: '🇱🇨', taxLabel: 'TIN', expectedLength: 10 },
  VC: { name: 'Saint Vincent and the Grenadines', flag: '🇻🇨', taxLabel: 'TIN', expectedLength: 10 },
  AW: { name: 'Aruba', flag: '🇦🇼', taxLabel: 'Persoonsnummer', expectedLength: 10 },
  CW: { name: 'Curaçao', flag: '🇨🇼', taxLabel: 'Crib-nummer', expectedLength: 9 },
  SX: { name: 'Sint Maarten', flag: '🇸🇽', taxLabel: 'Crib-nummer', expectedLength: 9 },
  KY: { name: 'Cayman Islands', flag: '🇰🇾', taxLabel: 'Tax ID', expectedLength: 9 },
  BM: { name: 'Bermuda', flag: '🇧🇲', taxLabel: 'Payroll Tax ID', expectedLength: 9 },
  VG: { name: 'British Virgin Islands', flag: '🇻🇬', taxLabel: 'TIN', expectedLength: 9 },
  TC: { name: 'Turks and Caicos Islands', flag: '🇹🇨', taxLabel: 'TIN', expectedLength: 9 },
  AI: { name: 'Anguilla', flag: '🇦🇮', taxLabel: 'TIN', expectedLength: 9 },
  MS: { name: 'Montserrat', flag: '🇲🇸', taxLabel: 'TIN', expectedLength: 9 },
  GL: { name: 'Greenland', flag: '🇬🇱', taxLabel: 'CVR / CPR', expectedLength: 8 },

  // ── South America
  BR: { name: 'Brazil', flag: '🇧🇷', taxLabel: 'CNPJ / CPF', expectedLength: 14 },
  AR: { name: 'Argentina', flag: '🇦🇷', taxLabel: 'CUIT', expectedLength: 11 },
  CO: { name: 'Colombia', flag: '🇨🇴', taxLabel: 'NIT', expectedLength: 10 },
  CL: { name: 'Chile', flag: '🇨🇱', taxLabel: 'RUT', expectedLength: 9 },
  PE: { name: 'Peru', flag: '🇵🇪', taxLabel: 'RUC', expectedLength: 11 },
  EC: { name: 'Ecuador', flag: '🇪🇨', taxLabel: 'RUC', expectedLength: 13 },
  BO: { name: 'Bolivia', flag: '🇧🇴', taxLabel: 'NIT', expectedLength: 10 },
  PY: { name: 'Paraguay', flag: '🇵🇾', taxLabel: 'RUC', expectedLength: 9 },
  UY: { name: 'Uruguay', flag: '🇺🇾', taxLabel: 'RUT', expectedLength: 12 },
  VE: { name: 'Venezuela', flag: '🇻🇪', taxLabel: 'RIF', expectedLength: 10 },
  GY: { name: 'Guyana', flag: '🇬🇾', taxLabel: 'TIN', expectedLength: 9 },
  SR: { name: 'Suriname', flag: '🇸🇷', taxLabel: 'FIN', expectedLength: 8 },

  // ── European Union (27 Member States)
  AT: { name: 'Austria', flag: '🇦🇹', taxLabel: 'UID / VAT ID', expectedLength: 9 },
  BE: { name: 'Belgium', flag: '🇧🇪', taxLabel: 'TVA / BTW', expectedLength: 10 },
  BG: { name: 'Bulgaria', flag: '🇧🇬', taxLabel: 'EIK / VAT ID', expectedLength: 10 },
  CY: { name: 'Cyprus', flag: '🇨🇾', taxLabel: 'TIC / VAT ID', expectedLength: 9 },
  CZ: { name: 'Czech Republic', flag: '🇨🇿', taxLabel: 'DIČ / VAT ID', expectedLength: 10 },
  DE: { name: 'Germany', flag: '🇩🇪', taxLabel: 'USt-IdNr. / Steuernummer', expectedLength: 9 },
  DK: { name: 'Denmark', flag: '🇩🇰', taxLabel: 'CVR / Moms', expectedLength: 8 },
  EE: { name: 'Estonia', flag: '🇪🇪', taxLabel: 'KMKR / VAT ID', expectedLength: 9 },
  EL: { name: 'Greece', flag: '🇬🇷', taxLabel: 'AFM / VAT ID', expectedLength: 9 },
  ES: { name: 'Spain', flag: '🇪🇸', taxLabel: 'NIF / CIF', expectedLength: 9 },
  FI: { name: 'Finland', flag: '🇫🇮', taxLabel: 'ALV-numero', expectedLength: 8 },
  FR: { name: 'France', flag: '🇫🇷', taxLabel: 'TVA / SIREN', expectedLength: 11 },
  HR: { name: 'Croatia', flag: '🇭🇷', taxLabel: 'OIB / PDV ID', expectedLength: 11 },
  HU: { name: 'Hungary', flag: '🇭🇺', taxLabel: 'Adószám', expectedLength: 8 },
  IE: { name: 'Ireland', flag: '🇮🇪', taxLabel: 'VAT Reg No.', expectedLength: 9 },
  IT: { name: 'Italy', flag: '🇮🇹', taxLabel: 'Partita IVA (P.IVA)', expectedLength: 11 },
  LT: { name: 'Lithuania', flag: '🇱🇹', taxLabel: 'PVM kodas', expectedLength: 12 },
  LU: { name: 'Luxembourg', flag: '🇱🇺', taxLabel: 'TVA / Matricule', expectedLength: 8 },
  LV: { name: 'Latvia', flag: '🇱🇻', taxLabel: 'PVN numurs', expectedLength: 11 },
  MT: { name: 'Malta', flag: '🇲🇹', taxLabel: 'VAT Number', expectedLength: 8 },
  NL: { name: 'Netherlands', flag: '🇳🇱', taxLabel: 'Btw-nummer', expectedLength: 12 },
  PL: { name: 'Poland', flag: '🇵🇱', taxLabel: 'NIP', expectedLength: 10 },
  PT: { name: 'Portugal', flag: '🇵🇹', taxLabel: 'NIF / NIPC', expectedLength: 9 },
  RO: { name: 'Romania', flag: '🇷🇴', taxLabel: 'CIF / CUI', expectedLength: 10 },
  SE: { name: 'Sweden', flag: '🇸🇪', taxLabel: 'Momsnr', expectedLength: 12 },
  SI: { name: 'Slovenia', flag: '🇸🇮', taxLabel: 'ID za DDV', expectedLength: 8 },
  SK: { name: 'Slovakia', flag: '🇸🇰', taxLabel: 'IČ DPH', expectedLength: 10 },

  // ── Non-EU Europe & Eurasia
  GB: { name: 'United Kingdom', flag: '🇬🇧', taxLabel: 'VAT Reg No.', expectedLength: 9 },
  CH: { name: 'Switzerland', flag: '🇨🇭', taxLabel: 'MWST / TVA / IVA / UID', expectedLength: 16 },
  NO: { name: 'Norway', flag: '🇳🇴', taxLabel: 'Orgnr / MVA', expectedLength: 9 },
  IS: { name: 'Iceland', flag: '🇮🇸', taxLabel: 'VSK-númer', expectedLength: 6 },
  TR: { name: 'Turkey', flag: '🇹🇷', taxLabel: 'VKN (Vergi Kimlik No)', expectedLength: 10 },
  RU: { name: 'Russia', flag: '🇷🇺', taxLabel: 'INN (ИНН)', expectedLength: 10 },
  UA: { name: 'Ukraine', flag: '🇺🇦', taxLabel: 'EDRPOU / IPN', expectedLength: 10 },
  RS: { name: 'Serbia', flag: '🇷🇸', taxLabel: 'PIB', expectedLength: 9 },
  BA: { name: 'Bosnia and Herzegovina', flag: '🇧🇦', taxLabel: 'JIB / PDV', expectedLength: 13 },
  ME: { name: 'Montenegro', flag: '🇲🇪', taxLabel: 'PIB', expectedLength: 8 },
  MK: { name: 'North Macedonia', flag: '🇲🇰', taxLabel: 'EDB', expectedLength: 13 },
  AL: { name: 'Albania', flag: '🇦🇱', taxLabel: 'NIPT', expectedLength: 10 },
  MD: { name: 'Moldova', flag: '🇲🇩', taxLabel: 'IDNO', expectedLength: 13 },
  GE: { name: 'Georgia', flag: '🇬🇪', taxLabel: 'TIN', expectedLength: 9 },
  AM: { name: 'Armenia', flag: '🇦🇲', taxLabel: 'TIN', expectedLength: 8 },
  AZ: { name: 'Azerbaijan', flag: '🇦🇿', taxLabel: 'VOEN', expectedLength: 10 },
  BY: { name: 'Belarus', flag: '🇧🇾', taxLabel: 'UNP', expectedLength: 9 },
  KZ: { name: 'Kazakhstan', flag: '🇰🇿', taxLabel: 'BIN / IIN', expectedLength: 12 },
  UZ: { name: 'Uzbekistan', flag: '🇺🇿', taxLabel: 'INN', expectedLength: 9 },
  AD: { name: 'Andorra', flag: '🇦🇩', taxLabel: 'NRT', expectedLength: 7 },
  LI: { name: 'Liechtenstein', flag: '🇱🇮', taxLabel: 'MWST / PEID', expectedLength: 9 },
  MC: { name: 'Monaco', flag: '🇲🇨', taxLabel: 'TVA / RCI', expectedLength: 9 },
  SM: { name: 'San Marino', flag: '🇸🇲', taxLabel: 'COE', expectedLength: 6 },
  VA: { name: 'Vatican City', flag: '🇻🇦', taxLabel: 'Codice Fiscale', expectedLength: 11 },
  GI: { name: 'Gibraltar', flag: '🇬🇮', taxLabel: 'TIN', expectedLength: 9 },
  IM: { name: 'Isle of Man', flag: '🇮🇲', taxLabel: 'VAT Number', expectedLength: 9 },
  JE: { name: 'Jersey', flag: '🇯🇪', taxLabel: 'TIN / GST', expectedLength: 9 },
  GG: { name: 'Guernsey', flag: '🇬🇬', taxLabel: 'TIN', expectedLength: 9 },
  FO: { name: 'Faroe Islands', flag: '🇫🇴', taxLabel: 'V-tal', expectedLength: 6 },
  XK: { name: 'Kosovo', flag: '🇽🇰', taxLabel: 'NUI / NRF', expectedLength: 9 },

  // ── Arab & Middle East
  AE: { name: 'United Arab Emirates', flag: '🇦🇪', taxLabel: 'TRN', expectedLength: 15 },
  SA: { name: 'Saudi Arabia', flag: '🇸🇦', taxLabel: 'VAT No.', expectedLength: 15 },
  BH: { name: 'Bahrain', flag: '🇧🇭', taxLabel: 'VAT Account No.', expectedLength: 15 },
  OM: { name: 'Oman', flag: '🇴🇲', taxLabel: 'VATIN', expectedLength: 12 },
  QA: { name: 'Qatar', flag: '🇶🇦', taxLabel: 'TIN', expectedLength: 8 },
  KW: { name: 'Kuwait', flag: '🇰🇼', taxLabel: 'TIN / Tax Card', expectedLength: 8 },
  EG: { name: 'Egypt', flag: '🇪🇬', taxLabel: 'TRN (بطاقة ضريبية)', expectedLength: 9 },
  IL: { name: 'Israel', flag: '🇮🇱', taxLabel: 'HP (ח.פ) / VAT ID', expectedLength: 9 },
  JO: { name: 'Jordan', flag: '🇯🇴', taxLabel: 'TIN', expectedLength: 8 },
  LB: { name: 'Lebanon', flag: '🇱🇧', taxLabel: 'MOF Tax No.', expectedLength: 8 },
  IQ: { name: 'Iraq', flag: '🇮🇶', taxLabel: 'TIN', expectedLength: 10 },
  IR: { name: 'Iran', flag: '🇮🇷', taxLabel: 'National Tax ID', expectedLength: 10 },
  SY: { name: 'Syria', flag: '🇸🇾', taxLabel: 'TIN', expectedLength: 9 },
  YE: { name: 'Yemen', flag: '🇾🇪', taxLabel: 'TIN', expectedLength: 8 },
  PS: { name: 'Palestine', flag: '🇵🇸', taxLabel: 'Tax ID', expectedLength: 9 },

  // ── South Asia
  IN: { name: 'India', flag: '🇮🇳', taxLabel: 'GSTIN', expectedLength: 15 },
  PK: { name: 'Pakistan', flag: '🇵🇰', taxLabel: 'NTN / STRN', expectedLength: 8 },
  BD: { name: 'Bangladesh', flag: '🇧🇩', taxLabel: 'BIN / e-TIN', expectedLength: 13 },
  LK: { name: 'Sri Lanka', flag: '🇱🇰', taxLabel: 'TIN / VAT', expectedLength: 9 },
  NP: { name: 'Nepal', flag: '🇳🇵', taxLabel: 'PAN / VAT', expectedLength: 9 },
  AF: { name: 'Afghanistan', flag: '🇦🇫', taxLabel: 'TIN', expectedLength: 10 },
  BT: { name: 'Bhutan', flag: '🇧🇹', taxLabel: 'TPN', expectedLength: 8 },
  MV: { name: 'Maldives', flag: '🇲🇻', taxLabel: 'TIN', expectedLength: 8 },

  // ── East & Southeast Asia
  CN: { name: 'China', flag: '🇨🇳', taxLabel: 'USCC (统一社会信用代码)', expectedLength: 18 },
  JP: { name: 'Japan', flag: '🇯🇵', taxLabel: 'JCT / Corporate Number', expectedLength: 13 },
  KR: { name: 'South Korea', flag: '🇰🇷', taxLabel: 'BRN (사업자등록번호)', expectedLength: 10 },
  TW: { name: 'Taiwan', flag: '🇹🇼', taxLabel: 'UBN (统一编号)', expectedLength: 8 },
  HK: { name: 'Hong Kong', flag: '🇭🇰', taxLabel: 'BRN (商业登记证号)', expectedLength: 8 },
  SG: { name: 'Singapore', flag: '🇸🇬', taxLabel: 'UEN / GST', expectedLength: 10 },
  MY: { name: 'Malaysia', flag: '🇲🇾', taxLabel: 'SST / MyInvois TIN', expectedLength: 12 },
  ID: { name: 'Indonesia', flag: '🇮🇩', taxLabel: 'NPWP', expectedLength: 16 },
  PH: { name: 'Philippines', flag: '🇵🇭', taxLabel: 'TIN', expectedLength: 12 },
  TH: { name: 'Thailand', flag: '🇹🇭', taxLabel: 'Tax ID (13 digits)', expectedLength: 13 },
  VN: { name: 'Vietnam', flag: '🇻🇳', taxLabel: 'Mã số thuế (MST)', expectedLength: 10 },
  MM: { name: 'Myanmar', flag: '🇲🇲', taxLabel: 'TIN', expectedLength: 10 },
  KH: { name: 'Cambodia', flag: '🇰🇭', taxLabel: 'VAT TIN', expectedLength: 9 },
  LA: { name: 'Laos', flag: '🇱🇦', taxLabel: 'Tax ID', expectedLength: 10 },
  BN: { name: 'Brunei', flag: '🇧🇳', taxLabel: 'Tax Ref', expectedLength: 8 },
  MO: { name: 'Macau', flag: '🇲🇴', taxLabel: 'NIF', expectedLength: 8 },
  MN: { name: 'Mongolia', flag: '🇲🇳', taxLabel: 'TT / RD', expectedLength: 10 },
  KP: { name: 'North Korea', flag: '🇰🇵', taxLabel: 'TIN', expectedLength: 8 },
  TL: { name: 'Timor-Leste', flag: '🇹🇱', taxLabel: 'TIN', expectedLength: 9 },
  TM: { name: 'Turkmenistan', flag: '🇹🇲', taxLabel: 'TIN', expectedLength: 10 },
  TJ: { name: 'Tajikistan', flag: '🇹🇯', taxLabel: 'TIN', expectedLength: 9 },
  KG: { name: 'Kyrgyzstan', flag: '🇰🇬', taxLabel: 'INN', expectedLength: 14 },

  // ── Africa (All 54 Countries)
  ZA: { name: 'South Africa', flag: '🇿🇦', taxLabel: 'VAT No.', expectedLength: 10 },
  KE: { name: 'Kenya', flag: '🇰🇪', taxLabel: 'KRA PIN', expectedLength: 11 },
  NG: { name: 'Nigeria', flag: '🇳🇬', taxLabel: 'TIN (FIRS/JTB)', expectedLength: 12 },
  GH: { name: 'Ghana', flag: '🇬🇭', taxLabel: 'TIN / Ghana Card', expectedLength: 11 },
  MA: { name: 'Morocco', flag: '🇲🇦', taxLabel: 'ICE / IF', expectedLength: 15 },
  DZ: { name: 'Algeria', flag: '🇩🇿', taxLabel: 'NIF', expectedLength: 15 },
  TN: { name: 'Tunisia', flag: '🇹🇳', taxLabel: 'Matricule Fiscal', expectedLength: 8 },
  ET: { name: 'Ethiopia', flag: '🇪🇹', taxLabel: 'TIN', expectedLength: 10 },
  TZ: { name: 'Tanzania', flag: '🇹🇿', taxLabel: 'TIN / VRN', expectedLength: 9 },
  UG: { name: 'Uganda', flag: '🇺🇬', taxLabel: 'TIN', expectedLength: 10 },
  RW: { name: 'Rwanda', flag: '🇷🇼', taxLabel: 'TIN', expectedLength: 9 },
  SN: { name: 'Senegal', flag: '🇸🇳', taxLabel: 'NINEA', expectedLength: 9 },
  CI: { name: 'Ivory Coast', flag: '🇨🇮', taxLabel: 'NCC / CC', expectedLength: 8 },
  CM: { name: 'Cameroon', flag: '🇨🇲', taxLabel: 'NIU', expectedLength: 14 },
  ZW: { name: 'Zimbabwe', flag: '🇿🇼', taxLabel: 'BP / VAT No.', expectedLength: 9 },
  ZM: { name: 'Zambia', flag: '🇿🇲', taxLabel: 'TPIN', expectedLength: 10 },
  AO: { name: 'Angola', flag: '🇦🇴', taxLabel: 'NIF', expectedLength: 10 },
  MZ: { name: 'Mozambique', flag: '🇲🇿', taxLabel: 'NUIT', expectedLength: 9 },
  MU: { name: 'Mauritius', flag: '🇲🇺', taxLabel: 'BRN / VAT', expectedLength: 8 },
  BW: { name: 'Botswana', flag: '🇧🇼', taxLabel: 'TIN', expectedLength: 9 },
  NA: { name: 'Namibia', flag: '🇳🇦', taxLabel: 'TIN', expectedLength: 10 },
  SZ: { name: 'Eswatini', flag: '🇸🇿', taxLabel: 'TIN', expectedLength: 8 },
  LS: { name: 'Lesotho', flag: '🇱🇸', taxLabel: 'TIN', expectedLength: 8 },
  MW: { name: 'Malawi', flag: '🇲🇼', taxLabel: 'TPIN', expectedLength: 8 },
  MG: { name: 'Madagascar', flag: '🇲🇬', taxLabel: 'NIF', expectedLength: 10 },
  SC: { name: 'Seychelles', flag: '🇸🇨', taxLabel: 'TIN', expectedLength: 9 },
  CV: { name: 'Cabo Verde', flag: '🇨🇻', taxLabel: 'NIF', expectedLength: 9 },
  ST: { name: 'Sao Tome and Principe', flag: '🇸🇹', taxLabel: 'NIF', expectedLength: 9 },
  KM: { name: 'Comoros', flag: '🇰🇲', taxLabel: 'NIF', expectedLength: 9 },
  DJ: { name: 'Djibouti', flag: '🇩🇯', taxLabel: 'NIF', expectedLength: 9 },
  SO: { name: 'Somalia', flag: '🇸🇴', taxLabel: 'TIN', expectedLength: 9 },
  ER: { name: 'Eritrea', flag: '🇪🇷', taxLabel: 'TIN', expectedLength: 9 },
  SS: { name: 'South Sudan', flag: '🇸🇸', taxLabel: 'TIN', expectedLength: 10 },
  SD: { name: 'Sudan', flag: '🇸🇩', taxLabel: 'TIN', expectedLength: 10 },
  TD: { name: 'Chad', flag: '🇹🇩', taxLabel: 'NIF', expectedLength: 9 },
  NE: { name: 'Niger', flag: '🇳🇪', taxLabel: 'NIF', expectedLength: 8 },
  ML: { name: 'Mali', flag: '🇲🇱', taxLabel: 'NIF', expectedLength: 9 },
  BF: { name: 'Burkina Faso', flag: '🇧🇫', taxLabel: 'IFU', expectedLength: 8 },
  MR: { name: 'Mauritania', flag: '🇲🇷', taxLabel: 'NIF', expectedLength: 8 },
  GN: { name: 'Guinea', flag: '🇬🇳', taxLabel: 'NIF', expectedLength: 9 },
  GW: { name: 'Guinea-Bissau', flag: '🇬🇼', taxLabel: 'NIF', expectedLength: 9 },
  SL: { name: 'Sierra Leone', flag: '🇸🇱', taxLabel: 'TIN', expectedLength: 10 },
  LR: { name: 'Liberia', flag: '🇱🇷', taxLabel: 'TIN', expectedLength: 10 },
  TG: { name: 'Togo', flag: '🇹🇬', taxLabel: 'NIF', expectedLength: 13 },
  BJ: { name: 'Benin', flag: '🇧🇯', taxLabel: 'IFU', expectedLength: 13 },
  GA: { name: 'Gabon', flag: '🇬🇦', taxLabel: 'NIF', expectedLength: 10 },
  CG: { name: 'Congo', flag: '🇨🇬', taxLabel: 'NIU', expectedLength: 10 },
  CD: { name: 'DR Congo', flag: '🇨🇩', taxLabel: 'Numéro Impôt', expectedLength: 10 },
  CF: { name: 'Central African Republic', flag: '🇨🇫', taxLabel: 'NIF', expectedLength: 9 },
  GQ: { name: 'Equatorial Guinea', flag: '🇬🇶', taxLabel: 'NIF', expectedLength: 9 },
  BI: { name: 'Burundi', flag: '🇧🇮', taxLabel: 'NIF', expectedLength: 10 },
  LY: { name: 'Libya', flag: '🇱🇾', taxLabel: 'TIN', expectedLength: 10 },

  // ── Oceania & Pacific
  AU: { name: 'Australia', flag: '🇦🇺', taxLabel: 'ABN', expectedLength: 11 },
  NZ: { name: 'New Zealand', flag: '🇳🇿', taxLabel: 'GST / IRD No.', expectedLength: 9 },
  FJ: { name: 'Fiji', flag: '🇫🇯', taxLabel: 'TIN', expectedLength: 9 },
  PG: { name: 'Papua New Guinea', flag: '🇵🇬', taxLabel: 'TIN', expectedLength: 8 },
  SB: { name: 'Solomon Islands', flag: '🇸🇧', taxLabel: 'TIN', expectedLength: 9 },
  VU: { name: 'Vanuatu', flag: '🇻🇺', taxLabel: 'CT Number', expectedLength: 8 },
  WS: { name: 'Samoa', flag: '🇼🇸', taxLabel: 'VAGST / TIN', expectedLength: 8 },
  TO: { name: 'Tonga', flag: '🇹🇴', taxLabel: 'TIN', expectedLength: 8 },
  TV: { name: 'Tuvalu', flag: '🇹🇻', taxLabel: 'TIN', expectedLength: 7 },
  KI: { name: 'Kiribati', flag: '🇰🇮', taxLabel: 'TIN', expectedLength: 7 },
  NR: { name: 'Nauru', flag: '🇳🇷', taxLabel: 'TIN', expectedLength: 7 },
  PW: { name: 'Palau', flag: '🇵🇼', taxLabel: 'TIN', expectedLength: 7 },
  FM: { name: 'Micronesia', flag: '🇫🇲', taxLabel: 'TIN', expectedLength: 7 },
  MH: { name: 'Marshall Islands', flag: '🇲🇭', taxLabel: 'TIN', expectedLength: 7 },
  CK: { name: 'Cook Islands', flag: '🇨🇰', taxLabel: 'RMD No.', expectedLength: 8 },
  NU: { name: 'Niue', flag: '🇳🇺', taxLabel: 'TIN', expectedLength: 7 },
  NC: { name: 'New Caledonia', flag: '🇳🇨', taxLabel: 'RIDET', expectedLength: 7 },
  PF: { name: 'French Polynesia', flag: '🇵🇫', taxLabel: 'N° TAHITI', expectedLength: 9 },
  AS: { name: 'American Samoa', flag: '🇦🇸', taxLabel: 'TIN', expectedLength: 9 },
  MP: { name: 'Northern Mariana Islands', flag: '🇲🇵', taxLabel: 'TIN', expectedLength: 9 },
};

export interface TaxIdInfo {
  countryCode: string;
  countryName: string;
  countryFlag: string;
  taxLabel: string;
  expectedLength: number;
  currentLength: number;
  isValid: boolean;
  isComplete: boolean;
  detectedRegion?: string;
  formatted: string;
}

// ── 3. Checksum & Format Algorithms ──────────────────────────────────────────

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const CHAR_SET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export function isValidGSTINChecksum(gstin: string): boolean {
  const g = gstin.trim().toUpperCase();
  if (!GSTIN_REGEX.test(g)) return false;
  if (!GST_STATE_CODES[g.slice(0, 2)]) return false;
  const mod = CHAR_SET.length;
  let factor = 2;
  let sum = 0;
  for (let i = g.length - 2; i >= 0; i--) {
    let codePoint = CHAR_SET.indexOf(g[i]) * factor;
    factor = factor === 2 ? 1 : 2;
    codePoint = Math.floor(codePoint / mod) + (codePoint % mod);
    sum += codePoint;
  }
  const checksum = (mod - (sum % mod)) % mod;
  return CHAR_SET[checksum] === g[g.length - 1];
}

export function isValidABNChecksum(abn: string): boolean {
  const clean = abn.replace(/\D/g, '');
  if (clean.length !== 11) return false;
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  let sum = (parseInt(clean[0], 10) - 1) * weights[0];
  for (let i = 1; i < 11; i++) {
    sum += parseInt(clean[i], 10) * weights[i];
  }
  return sum % 89 === 0;
}

export function isValidUKVAT(vat: string): boolean {
  const clean = vat.replace(/^GB/, '').replace(/\s+/g, '');
  
  // UK VAT numbers are either 9 digits (standard) or 12 digits (branch traders)
  if (!/^\d{9}$/.test(clean) && !/^\d{12}$/.test(clean)) return false;
  
  const base = clean.slice(0, 9);
  let total = 0;
  for (let i = 0; i < 7; i++) {
    total += parseInt(base[i], 10) * (8 - i);
  }
  
  const cd = parseInt(base.slice(7, 9), 10);
  
  // HMRC Rule 1 (Pre-Nov 2009 standard Modulus 97)
  let check1 = 97 - (total % 97);
  if (check1 === 97) check1 = 0;
  if (check1 === cd) return true;
  
  // HMRC Rule 2 (Post-Nov 2009 Modulus 97 with +55 offset)
  let check2 = (check1 + 42) % 97;
  if (check2 === cd) return true;
  
  return false;
}

const EU_VAT_RULES: Record<string, { regex: RegExp; name: string; flag: string }> = {
  AT: { regex: /^ATU\d{8}$/, name: 'Austria', flag: '🇦🇹' },
  BE: { regex: /^BE[01]\d{9}$/, name: 'Belgium', flag: '🇧🇪' },
  BG: { regex: /^BG\d{9,10}$/, name: 'Bulgaria', flag: '🇧🇬' },
  CY: { regex: /^CY\d{8}[A-Z]$/, name: 'Cyprus', flag: '🇨🇾' },
  CZ: { regex: /^CZ\d{8,10}$/, name: 'Czech Republic', flag: '🇨🇿' },
  DE: { regex: /^DE\d{9}$/, name: 'Germany', flag: '🇩🇪' },
  DK: { regex: /^DK\d{8}$/, name: 'Denmark', flag: '🇩🇰' },
  EE: { regex: /^EE\d{9}$/, name: 'Estonia', flag: '🇪🇪' },
  EL: { regex: /^EL\d{9}$/, name: 'Greece', flag: '🇬🇷' },
  ES: { regex: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/, name: 'Spain', flag: '🇪🇸' },
  FI: { regex: /^FI\d{8}$/, name: 'Finland', flag: '🇫🇮' },
  FR: { regex: /^FR[A-Z0-9]{2}\d{9}$/, name: 'France', flag: '🇫🇷' },
  HR: { regex: /^HR\d{11}$/, name: 'Croatia', flag: '🇭🇷' },
  HU: { regex: /^HU\d{8}$/, name: 'Hungary', flag: '🇭🇺' },
  IE: { regex: /^IE\d[A-Z0-9+*]\d{5}[A-Z]$/, name: 'Ireland', flag: '🇮🇪' },
  IT: { regex: /^IT\d{11}$/, name: 'Italy', flag: '🇮🇹' },
  LT: { regex: /^LT(\d{9}|\d{12})$/, name: 'Lithuania', flag: '🇱🇹' },
  LU: { regex: /^LU\d{8}$/, name: 'Luxembourg', flag: '🇱🇺' },
  LV: { regex: /^LV\d{11}$/, name: 'Latvia', flag: '🇱🇻' },
  MT: { regex: /^MT\d{8}$/, name: 'Malta', flag: '🇲🇹' },
  NL: { regex: /^NL\d{9}B\d{2}$/, name: 'Netherlands', flag: '🇳🇱' },
  PL: { regex: /^PL\d{10}$/, name: 'Poland', flag: '🇵🇱' },
  PT: { regex: /^PT\d{9}$/, name: 'Portugal', flag: '🇵🇹' },
  RO: { regex: /^RO\d{2,10}$/, name: 'Romania', flag: '🇷🇴' },
  SE: { regex: /^SE\d{12}$/, name: 'Sweden', flag: '🇸🇪' },
  SI: { regex: /^SI\d{8}$/, name: 'Slovenia', flag: '🇸🇮' },
  SK: { regex: /^SK\d{10}$/, name: 'Slovakia', flag: '🇸🇰' },
};

// ── 4. Master Universal Global Inspector ─────────────────────────────────────

export function inspectTaxId(raw: string, outletCountryFallback: string = 'IN'): TaxIdInfo {
  const upperRaw = (raw || '').trim().toUpperCase();
  const clean = upperRaw.replace(/[\s\-\.]/g, '');
  const currentLength = clean.length;

  if (!clean) {
    const fallbackMeta = GLOBAL_COUNTRIES[outletCountryFallback] || GLOBAL_COUNTRIES['IN'];
    return {
      countryCode: outletCountryFallback,
      countryName: fallbackMeta.name,
      countryFlag: fallbackMeta.flag,
      taxLabel: fallbackMeta.taxLabel,
      expectedLength: fallbackMeta.expectedLength || 15,
      currentLength: 0,
      isValid: false,
      isComplete: false,
      formatted: '',
    };
  }

  // 1. CANADA
  const isExplicitCanada = upperRaw.startsWith('CA-') || upperRaw.startsWith('CA ');
  const hasCaRtTq = clean.includes('RT') || clean.includes('TQ');
  const hasCaPst = upperRaw.startsWith('PST') || upperRaw.startsWith('RST');

  if (
    outletCountryFallback === 'CA' ||
    isExplicitCanada ||
    hasCaRtTq ||
    hasCaPst ||
    (clean.startsWith('CA') && clean.length >= 4 && CANADA_PROVINCES[clean.slice(2, 4)])
  ) {
    let detectedProvince: string | undefined;
    if (clean.includes('TQ') || clean.startsWith('QC') || upperRaw.startsWith('QST') || upperRaw.startsWith('TVQ')) {
      detectedProvince = 'Quebec';
    } else if (clean.startsWith('BC') || upperRaw.startsWith('PST-BC') || upperRaw.startsWith('PSTBC') || upperRaw.startsWith('BC-')) {
      detectedProvince = 'British Columbia';
    } else if (clean.startsWith('SK') || upperRaw.startsWith('PST-SK') || upperRaw.startsWith('SK-')) {
      detectedProvince = 'Saskatchewan';
    } else if (clean.startsWith('MB') || upperRaw.startsWith('RST-MB') || upperRaw.startsWith('MB-') || upperRaw.startsWith('RST')) {
      detectedProvince = 'Manitoba';
    } else {
      const candidateCode = isExplicitCanada ? clean.slice(2, 4) : clean.slice(0, 2);
      if (CANADA_PROVINCES[candidateCode]) {
        detectedProvince = CANADA_PROVINCES[candidateCode];
      }
    }
    const isComplete = currentLength === 9 || currentLength === 15;
    const isValid = /^\d{9}(RT\d{4})?$/.test(clean) || /^\d{10}TQ\d{4}$/.test(clean) || (currentLength >= 7 && currentLength <= 15);
    return {
      countryCode: 'CA',
      countryName: 'Canada',
      countryFlag: '🇨🇦',
      taxLabel: detectedProvince ? `${detectedProvince} Tax ID` : 'GST/HST No.',
      expectedLength: clean.includes('RT') || clean.includes('TQ') ? 15 : 9,
      currentLength,
      isValid,
      isComplete,
      detectedRegion: detectedProvince,
      formatted: clean,
    };
  }

  // 2. USA
  const isExplicitUS = upperRaw.startsWith('US-') || upperRaw.startsWith('US ') || upperRaw.startsWith('USA-');
  const isEINFormat = /^\d{2}-\d{7}$/.test(upperRaw) || upperRaw.startsWith('EIN');

  let candidateStateCode = '';
  if (isExplicitUS) {
    candidateStateCode = clean.slice(2, 4);
  } else if (upperRaw.startsWith('SR-') || upperRaw.startsWith('SRCA')) {
    candidateStateCode = 'CA';
  } else if (/^[A-Z]{2}[-\s]?\d+/.test(upperRaw)) {
    const directCode = clean.slice(0, 2);
    if (US_STATES[directCode] && directCode !== 'IN' && !EU_VAT_RULES[directCode]) {
      candidateStateCode = directCode;
    }
  }

  if (candidateStateCode && US_STATES[candidateStateCode]) {
    const stateName = US_STATES[candidateStateCode];
    const isComplete = currentLength >= 7;
    return {
      countryCode: 'US',
      countryName: 'United States',
      countryFlag: '🇺🇸',
      taxLabel: `${stateName} Sales Tax ID`,
      expectedLength: 11,
      currentLength,
      isValid: isComplete,
      isComplete,
      detectedRegion: stateName,
      formatted: upperRaw,
    };
  }

  if (
    isEINFormat ||
    (outletCountryFallback === 'US' && /^\d{9}$/.test(clean)) ||
    (isExplicitUS && /^\d{9}$/.test(clean.slice(2)))
  ) {
    const digits = clean.replace(/\D/g, '');
    const isComplete = digits.length === 9;
    return {
      countryCode: 'US',
      countryName: 'United States',
      countryFlag: '🇺🇸',
      taxLabel: 'EIN / Tax ID',
      expectedLength: 9,
      currentLength: digits.length,
      isValid: isComplete,
      isComplete,
      formatted: digits.length === 9 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits,
    };
  }

  // 3. JAPAN
  if (upperRaw.startsWith('JP') || (clean.startsWith('T') && /^\d{13}$/.test(clean.slice(1))) || (outletCountryFallback === 'JP' && /^\d{13}$/.test(clean))) {
    const isComplete = currentLength >= 13;
    return {
      countryCode: 'JP',
      countryName: 'Japan',
      countryFlag: '🇯🇵',
      taxLabel: clean.startsWith('T') ? 'Qualified Invoice JCT No.' : 'Corporate Number',
      expectedLength: 13,
      currentLength,
      isValid: isComplete,
      isComplete,
      formatted: clean,
    };
  }

  // 4. SOUTH KOREA
  if (upperRaw.startsWith('KR') || (outletCountryFallback === 'KR' && /^\d{10}$/.test(clean))) {
    const digits = clean.replace(/\D/g, '');
    const prefix3 = digits.slice(0, 3);
    const detectedDistrict = KOREA_DISTRICTS[prefix3];
    const isComplete = digits.length === 10;
    return {
      countryCode: 'KR',
      countryName: 'South Korea',
      countryFlag: '🇰🇷',
      taxLabel: detectedDistrict ? `BRN (${detectedDistrict})` : 'BRN (사업자등록번호)',
      expectedLength: 10,
      currentLength: digits.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: detectedDistrict,
      formatted: digits.length === 10 ? `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}` : digits,
    };
  }

  // 5. CHINA
  if (upperRaw.startsWith('CN') || (outletCountryFallback === 'CN' && currentLength === 18) || (currentLength === 18 && /^[0-9A-HJ-NP-RT-UW-Y]{18}$/.test(clean) && outletCountryFallback !== 'IN')) {
    const pure = clean.replace(/^CN/, '');
    const isComplete = pure.length === 18;
    const provCode = pure.length === 18 ? pure.slice(2, 4) : undefined;
    const detectedProvince = provCode ? CHINA_PROVINCES[provCode] : undefined;

    return {
      countryCode: 'CN',
      countryName: 'China',
      countryFlag: '🇨🇳',
      taxLabel: detectedProvince ? `USCC (${detectedProvince})` : 'USCC (统一社会信用代码)',
      expectedLength: 18,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: detectedProvince,
      formatted: pure,
    };
  }

  // 6. RUSSIA
  if (upperRaw.startsWith('RU') || (outletCountryFallback === 'RU' && (currentLength === 10 || currentLength === 12))) {
    const pure = clean.replace(/^RU/, '');
    const isComplete = pure.length === 10 || pure.length === 12;
    const regionCode = pure.slice(0, 2);
    const detectedRegion = RUSSIA_REGIONS[regionCode];
    return {
      countryCode: 'RU',
      countryName: 'Russia',
      countryFlag: '🇷🇺',
      taxLabel: detectedRegion ? `INN (${detectedRegion})` : 'INN (ИНН)',
      expectedLength: 10,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion,
      formatted: pure,
    };
  }

  // 7. NEW ZEALAND
  if (upperRaw.startsWith('NZ') || (outletCountryFallback === 'NZ' && (currentLength === 8 || currentLength === 9))) {
    const pure = clean.replace(/^NZ/, '');
    const isComplete = pure.length === 8 || pure.length === 9;
    return {
      countryCode: 'NZ',
      countryName: 'New Zealand',
      countryFlag: '🇳🇿',
      taxLabel: 'GST / IRD No.',
      expectedLength: 9,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      formatted: pure,
    };
  }

  // 8. TURKEY
  if (upperRaw.startsWith('TR') || (outletCountryFallback === 'TR' && /^\d{10}$/.test(clean))) {
    const pure = clean.replace(/^TR/, '');
    const isComplete = pure.length === 10;
    return {
      countryCode: 'TR',
      countryName: 'Turkey',
      countryFlag: '🇹🇷',
      taxLabel: 'VKN (Vergi Kimlik No)',
      expectedLength: 10,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      formatted: pure,
    };
  }

  // 9. NORWAY
  if (upperRaw.startsWith('NO') || clean.endsWith('MVA') || (outletCountryFallback === 'NO' && /^\d{9}$/.test(clean))) {
    let detectedRegion: string | undefined;
    for (const [code, name] of Object.entries(NORWAY_REGIONS)) {
      if (upperRaw.includes(code)) {
        detectedRegion = name;
        break;
      }
    }
    const pure = clean.replace(/^NO/, '').replace(/MVA$/, '');
    const isComplete = pure.length === 9;
    return {
      countryCode: 'NO',
      countryName: 'Norway',
      countryFlag: '🇳🇴',
      taxLabel: detectedRegion ? 'MVA (0% Svalbard Exempt)' : 'Orgnr / MVA',
      expectedLength: 9,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion,
      formatted: `${pure} MVA`,
    };
  }

  // 10. ISRAEL
  if (upperRaw.startsWith('IL') || upperRaw.includes('EILAT') || (outletCountryFallback === 'IL' && /^\d{9}$/.test(clean))) {
    const isEilat = upperRaw.includes('EILAT') || upperRaw.includes('ETH');
    const pure = clean.replace(/^IL/, '').replace(/^(EILAT|ETH)/, '');
    const isComplete = pure.length === 9;
    return {
      countryCode: 'IL',
      countryName: 'Israel',
      countryFlag: '🇮🇱',
      taxLabel: isEilat ? 'HP (0% VAT Eilat Zone)' : 'HP (ח.פ) / VAT ID',
      expectedLength: 9,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: isEilat ? 'Eilat Free Trade Zone' : undefined,
      formatted: pure,
    };
  }

  // 11. PAKISTAN
  if (
    upperRaw.startsWith('PK') ||
    upperRaw.startsWith('SRB') ||
    upperRaw.startsWith('PRA') ||
    upperRaw.startsWith('KPRA') ||
    upperRaw.startsWith('BRA') ||
    (outletCountryFallback === 'PK' && /^\d+$/.test(clean))
  ) {
    let detectedProv: string | undefined;
    for (const [code, name] of Object.entries(PAKISTAN_PROVINCES)) {
      if (upperRaw.includes(code)) {
        detectedProv = name;
        break;
      }
    }
    const pure = clean.replace(/^PK/, '').replace(/^(SRB|PRA|KPRA|BRA)/, '');
    const isComplete = pure.length >= 7;
    return {
      countryCode: 'PK',
      countryName: 'Pakistan',
      countryFlag: '🇵🇰',
      taxLabel: detectedProv ? `Services Tax (${detectedProv})` : pure.length >= 12 ? 'STRN' : 'NTN',
      expectedLength: 8,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: detectedProv,
      formatted: pure,
    };
  }

  // 12. BANGLADESH
  if (upperRaw.startsWith('BD') || (outletCountryFallback === 'BD' && /^\d+$/.test(clean))) {
    const pure = clean.replace(/^BD/, '');
    const isComplete = pure.length === 9 || pure.length === 12 || pure.length === 13;
    return {
      countryCode: 'BD',
      countryName: 'Bangladesh',
      countryFlag: '🇧🇩',
      taxLabel: pure.length >= 12 ? 'BIN' : 'e-TIN',
      expectedLength: 13,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      formatted: pure,
    };
  }

  // 13. TAIWAN
  if (upperRaw.startsWith('TW') || (outletCountryFallback === 'TW' && /^\d{8}$/.test(clean))) {
    const pure = clean.replace(/^TW/, '');
    const isComplete = pure.length === 8;
    return {
      countryCode: 'TW',
      countryName: 'Taiwan',
      countryFlag: '🇹🇼',
      taxLabel: 'UBN (统一编号)',
      expectedLength: 8,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      formatted: pure,
    };
  }

  // 14. HONG KONG
  if (upperRaw.startsWith('HK') || (outletCountryFallback === 'HK' && /^\d{8}$/.test(clean))) {
    const pure = clean.replace(/^HK/, '');
    const isComplete = pure.length === 8;
    return {
      countryCode: 'HK',
      countryName: 'Hong Kong',
      countryFlag: '🇭🇰',
      taxLabel: 'BRN (商业登记证号)',
      expectedLength: 8,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      formatted: pure,
    };
  }

  // 14A. MALAYSIA
  if (upperRaw.startsWith('MY') || /^[A-Z]\d{2}-\d{4}-\d{8}$/.test(upperRaw) || /^C\d{10}$/.test(clean) || (outletCountryFallback === 'MY' && currentLength >= 10)) {
    let detectedRegion: string | undefined;
    for (const [code, name] of Object.entries(MALAYSIA_REGIONS)) {
      if (upperRaw.includes(code)) {
        detectedRegion = name;
        break;
      }
    }
    const isComplete = currentLength >= 10;
    return {
      countryCode: 'MY',
      countryName: 'Malaysia',
      countryFlag: '🇲🇾',
      taxLabel: detectedRegion?.includes('0%') ? 'SST (0% Duty-Free)' : 'SST / MyInvois TIN',
      expectedLength: 12,
      currentLength,
      isValid: isComplete,
      isComplete,
      detectedRegion,
      formatted: upperRaw,
    };
  }

  // 14B. PHILIPPINES
  if (upperRaw.startsWith('PH') || upperRaw.includes('PEZA') || (outletCountryFallback === 'PH' && /^\d+$/.test(clean))) {
    let detectedRegion: string | undefined;
    for (const [code, name] of Object.entries(PHILIPPINES_REGIONS)) {
      if (upperRaw.includes(code)) {
        detectedRegion = name;
        break;
      }
    }
    const pure = clean.replace(/^PH/, '');
    const isComplete = pure.length === 9 || pure.length === 12;
    return {
      countryCode: 'PH',
      countryName: 'Philippines',
      countryFlag: '🇵🇭',
      taxLabel: detectedRegion?.includes('0%') ? 'TIN (0% PEZA Zone)' : 'TIN',
      expectedLength: 12,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion,
      formatted: pure,
    };
  }

  // 14C. THAILAND
  if (upperRaw.startsWith('TH') || (outletCountryFallback === 'TH' && (currentLength === 13 || currentLength === 18))) {
    const pure = clean.replace(/^TH/, '');
    const isWithBranch = pure.length === 18;
    const branchCode = isWithBranch ? pure.slice(13) : undefined;
    const branchLabel = branchCode === '00000' ? 'Head Office (สำนักงานใหญ่)' : branchCode ? `Branch #${branchCode}` : undefined;
    const isComplete = pure.length === 13 || pure.length === 18;

    return {
      countryCode: 'TH',
      countryName: 'Thailand',
      countryFlag: '🇹🇭',
      taxLabel: branchLabel ? `Tax ID (${branchLabel})` : 'Tax ID (เลขประจำตัวผู้เสียภาษี)',
      expectedLength: 13,
      currentLength: pure.length,
      isValid: isComplete && /^\d+$/.test(pure),
      isComplete,
      detectedRegion: branchLabel,
      formatted: isWithBranch ? `${pure.slice(0, 13)} (${pure.slice(13)})` : pure,
    };
  }

  // 15. SPAIN & PORTUGAL
  if (clean.startsWith('ES') || upperRaw.startsWith('ES-') || (outletCountryFallback === 'ES' && currentLength >= 8)) {
    let detectedRegion: string | undefined;
    const cleanNoEs = clean.replace(/^ES/, '');
    const prefix2 = upperRaw.startsWith('ES-') ? upperRaw.slice(3, 5) : cleanNoEs.slice(0, 2);
    const prefix3 = upperRaw.startsWith('ES-') ? upperRaw.slice(3, 6) : cleanNoEs.slice(0, 3);
    if (SPAIN_SPECIAL_REGIONS[prefix2]) detectedRegion = SPAIN_SPECIAL_REGIONS[prefix2];
    else if (SPAIN_SPECIAL_REGIONS[prefix3]) detectedRegion = SPAIN_SPECIAL_REGIONS[prefix3];

    const isComplete = clean.length >= 9;
    return {
      countryCode: 'ES',
      countryName: 'Spain',
      countryFlag: '🇪🇸',
      taxLabel: detectedRegion?.includes('IGIC') ? 'Canary Islands IGIC' : detectedRegion?.includes('IPSI') ? 'IPSI No.' : 'NIF / CIF',
      expectedLength: 9,
      currentLength: clean.length,
      isValid: isComplete,
      isComplete,
      detectedRegion,
      formatted: clean,
    };
  }

  if (clean.startsWith('PT') || upperRaw.startsWith('PT-') || (outletCountryFallback === 'PT' && currentLength >= 9)) {
    let detectedRegion: string | undefined;
    const cleanNoPt = clean.replace(/^PT/, '');
    const prefix2 = upperRaw.startsWith('PT-') ? upperRaw.slice(3, 5) : cleanNoPt.slice(0, 2);
    const prefix3 = upperRaw.startsWith('PT-') ? upperRaw.slice(3, 6) : cleanNoPt.slice(0, 3);
    if (PORTUGAL_REGIONS[prefix2]) detectedRegion = PORTUGAL_REGIONS[prefix2];
    else if (PORTUGAL_REGIONS[prefix3]) detectedRegion = PORTUGAL_REGIONS[prefix3];

    const isComplete = clean.length >= 9;
    return {
      countryCode: 'PT',
      countryName: 'Portugal',
      countryFlag: '🇵🇹',
      taxLabel: detectedRegion ? `NIF (${detectedRegion})` : 'NIF / NIPC',
      expectedLength: 9,
      currentLength: clean.length,
      isValid: isComplete,
      isComplete,
      detectedRegion,
      formatted: clean,
    };
  }

  // Northern Ireland Protocol
  if (clean.startsWith('XI')) {
    const isComplete = clean.length === 11;
    return {
      countryCode: 'GB',
      countryName: 'United Kingdom (Northern Ireland)',
      countryFlag: '🇬🇧',
      taxLabel: 'VAT ID (Northern Ireland)',
      expectedLength: 11,
      currentLength: clean.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: 'Northern Ireland (EU Dual Protocol)',
      formatted: clean,
    };
  }

  // France: DOM-TOM
  if (clean.startsWith('FR') || upperRaw.startsWith('FR-') || (outletCountryFallback === 'FR' && currentLength >= 9)) {
    let detectedRegion: string | undefined;
    for (const [dept, name] of Object.entries(FRANCE_DOM_REGIONS)) {
      if (upperRaw.includes(dept)) {
        detectedRegion = name;
        break;
      }
    }
    const isComplete = clean.length >= 11;
    return {
      countryCode: 'FR',
      countryName: 'France',
      countryFlag: '🇫🇷',
      taxLabel: detectedRegion ? `TVA (${detectedRegion})` : 'TVA / SIREN',
      expectedLength: 11,
      currentLength: clean.length,
      isValid: isComplete,
      isComplete,
      detectedRegion,
      formatted: clean,
    };
  }

  // Greece: Aegean Islands
  if (clean.startsWith('EL') || clean.startsWith('GR') || (outletCountryFallback === 'GR' && currentLength >= 9)) {
    let detectedRegion: string | undefined;
    for (const [code, name] of Object.entries(GREECE_ISLANDS)) {
      if (upperRaw.includes(code)) {
        detectedRegion = name;
        break;
      }
    }
    const isComplete = clean.length >= 9;
    return {
      countryCode: 'EL',
      countryName: 'Greece',
      countryFlag: '🇬🇷',
      taxLabel: detectedRegion ? `AFM (${detectedRegion})` : 'AFM / VAT ID',
      expectedLength: 9,
      currentLength: clean.length,
      isValid: isComplete,
      isComplete,
      detectedRegion,
      formatted: clean,
    };
  }

  // Italy
  if (clean.startsWith('IT') || (outletCountryFallback === 'IT' && /^\d{11}$/.test(clean))) {
    let detectedRegion: string | undefined;
    for (const [code, name] of Object.entries(ITALY_SPECIAL_REGIONS)) {
      if (upperRaw.includes(code)) {
        detectedRegion = name;
        break;
      }
    }
    const pure = clean.replace(/^IT/, '');
    const isComplete = pure.length === 11;
    return {
      countryCode: 'IT',
      countryName: 'Italy',
      countryFlag: '🇮🇹',
      taxLabel: detectedRegion?.includes('0%') ? 'P.IVA (0% Livigno Exempt)' : detectedRegion ? `P.IVA (${detectedRegion})` : 'Partita IVA (P.IVA)',
      expectedLength: 11,
      currentLength: pure.length,
      isValid: isComplete && /^\d{11}$/.test(pure),
      isComplete,
      detectedRegion,
      formatted: clean,
    };
  }

  // Finland
  if (clean.startsWith('FI') || clean.startsWith('AX') || (outletCountryFallback === 'FI' && /^\d{8}$/.test(clean))) {
    let detectedRegion: string | undefined;
    for (const [code, name] of Object.entries(FINLAND_REGIONS)) {
      if (upperRaw.includes(code)) {
        detectedRegion = name;
        break;
      }
    }
    const pure = clean.replace(/^(FI|AX)/, '');
    const isComplete = pure.length === 8;
    return {
      countryCode: clean.startsWith('AX') ? 'AX' : 'FI',
      countryName: clean.startsWith('AX') ? 'Åland Islands' : 'Finland',
      countryFlag: clean.startsWith('AX') ? '🇦🇽' : '🇫🇮',
      taxLabel: detectedRegion ? 'ALV (0% Åland Customs Zone)' : 'ALV-numero',
      expectedLength: 8,
      currentLength: pure.length,
      isValid: isComplete && /^\d{8}$/.test(pure),
      isComplete,
      detectedRegion,
      formatted: clean,
    };
  }

  // Germany
  if (clean.startsWith('DE') || (outletCountryFallback === 'DE' && /^\d{9}$/.test(clean))) {
    let detectedState: string | undefined;
    for (const [code, name] of Object.entries(GERMANY_STATES)) {
      if (upperRaw.includes(code)) {
        detectedState = name;
        break;
      }
    }
    const pure = clean.replace(/^DE/, '');
    const isComplete = pure.length === 9;
    return {
      countryCode: 'DE',
      countryName: 'Germany',
      countryFlag: '🇩🇪',
      taxLabel: detectedState?.includes('0%') ? 'USt-IdNr. (Duty-Free)' : detectedState ? `USt-IdNr. (${detectedState})` : 'USt-IdNr.',
      expectedLength: 9,
      currentLength: pure.length,
      isValid: isComplete && /^\d{9}$/.test(pure),
      isComplete,
      detectedRegion: detectedState,
      formatted: clean,
    };
  }

  // Austria
  if (clean.startsWith('AT') || (outletCountryFallback === 'AT' && /^U\d{8}$/.test(clean))) {
    let detectedEnclave: string | undefined;
    for (const [code, name] of Object.entries(AUSTRIA_SPECIAL_REGIONS)) {
      if (upperRaw.includes(code)) {
        detectedEnclave = name;
        break;
      }
    }
    const pure = clean.replace(/^AT/, '');
    const isComplete = pure.length === 9;
    return {
      countryCode: 'AT',
      countryName: 'Austria',
      countryFlag: '🇦🇹',
      taxLabel: detectedEnclave ? `UID (${detectedEnclave})` : 'UID / VAT ID',
      expectedLength: 9,
      currentLength: pure.length,
      isValid: isComplete && /^U\d{8}$/.test(pure),
      isComplete,
      detectedRegion: detectedEnclave,
      formatted: clean,
    };
  }

  // Denmark
  if (clean.startsWith('DK') || clean.startsWith('GL') || clean.startsWith('FO') || (outletCountryFallback === 'DK' && /^\d{8}$/.test(clean))) {
    let detectedTerritory: string | undefined;
    for (const [code, name] of Object.entries(DENMARK_REGIONS)) {
      if (upperRaw.includes(code)) {
        detectedTerritory = name;
        break;
      }
    }
    const pure = clean.replace(/^(DK|GL|FO)/, '');
    const isComplete = pure.length === 8;
    return {
      countryCode: clean.startsWith('GL') ? 'GL' : clean.startsWith('FO') ? 'FO' : 'DK',
      countryName: clean.startsWith('GL') ? 'Greenland' : clean.startsWith('FO') ? 'Faroe Islands' : 'Denmark',
      countryFlag: clean.startsWith('GL') ? '🇬🇱' : clean.startsWith('FO') ? '🇫🇴' : '🇩🇰',
      taxLabel: detectedTerritory ? detectedTerritory : 'CVR / Moms',
      expectedLength: 8,
      currentLength: pure.length,
      isValid: isComplete && /^\d{8}$/.test(pure),
      isComplete,
      detectedRegion: detectedTerritory,
      formatted: clean,
    };
  }

  // European Union (27 countries)
  const euPrefix = clean.slice(0, 2);
  if (EU_VAT_RULES[euPrefix]) {
    const rule = EU_VAT_RULES[euPrefix];
    const isComplete = currentLength >= 8;
    const isValid = rule.regex.test(clean);
    return {
      countryCode: euPrefix,
      countryName: rule.name,
      countryFlag: rule.flag,
      taxLabel: 'VAT ID',
      expectedLength: 11,
      currentLength,
      isValid,
      isComplete,
      formatted: clean,
    };
  }

  // 16. UNITED KINGDOM
  if (clean.startsWith('GB') || (outletCountryFallback === 'GB' && /^\d+$/.test(clean))) {
    let detectedDevolved: string | undefined;
    for (const [code, name] of Object.entries(UK_DEVOLVED_REGIONS)) {
      if (upperRaw.includes(code)) {
        detectedDevolved = name;
        break;
      }
    }
    const pure = clean.replace(/^GB/, '');
    const isComplete = pure.length === 9 || pure.length === 12;
    const isValid = isComplete && /^\d+$/.test(pure);
    return {
      countryCode: 'GB',
      countryName: 'United Kingdom',
      countryFlag: '🇬🇧',
      taxLabel: detectedDevolved ? `VAT (${detectedDevolved})` : 'VAT Reg No.',
      expectedLength: clean.startsWith('GB') ? 11 : 9,
      currentLength,
      isValid,
      isComplete,
      detectedRegion: detectedDevolved,
      formatted: clean,
    };
  }

  // 17. SWITZERLAND
  if (outletCountryFallback === 'CH' || clean.startsWith('CHE') || upperRaw.startsWith('CH-')) {
    let detectedCanton: string | undefined;
    for (const [code, name] of Object.entries(SWISS_CANTONS)) {
      if (upperRaw.includes(`-${code}-`) || upperRaw.startsWith(`CH-${code}`) || upperRaw.endsWith(`-${code}`)) {
        detectedCanton = name;
        break;
      }
    }
    const isComplete = /^CHE\d{9}(MWST|TVA|IVA)?$/.test(clean) || clean.length >= 9;
    return {
      countryCode: 'CH',
      countryName: 'Switzerland',
      countryFlag: '🇨🇭',
      taxLabel: detectedCanton ? `UID (${detectedCanton})` : 'MWST / TVA / IVA / UID',
      expectedLength: 16,
      currentLength,
      isValid: isComplete,
      isComplete,
      detectedRegion: detectedCanton,
      formatted: clean,
    };
  }

  // 18. AUSTRALIA
  const isAUPrefix = clean.startsWith('AU');
  const pureAUDigits = clean.replace(/^AU/, '');
  if (isAUPrefix || outletCountryFallback === 'AU' || (currentLength === 11 && /^\d{11}$/.test(clean) && outletCountryFallback !== 'IN')) {
    const isComplete = pureAUDigits.length === 11;
    let detectedAUState: string | undefined;
    const candidateAuState = upperRaw.slice(0, 3);
    if (AUSTRALIA_STATES[candidateAuState]) {
      detectedAUState = AUSTRALIA_STATES[candidateAuState];
    }
    return {
      countryCode: 'AU',
      countryName: 'Australia',
      countryFlag: '🇦🇺',
      taxLabel: 'ABN',
      expectedLength: 11,
      currentLength: pureAUDigits.length,
      isValid: isComplete && /^\d{11}$/.test(pureAUDigits),
      isComplete,
      detectedRegion: detectedAUState,
      formatted: pureAUDigits,
    };
  }

  // 19. BRAZIL
  if (upperRaw.startsWith('BR') || (outletCountryFallback === 'BR' && /^\d+$/.test(clean))) {
    const pure = clean.replace(/^BR/, '');
    const isComplete = pure.length === 14 || pure.length === 11;
    let detectedState: string | undefined;
    const directCode = upperRaw.slice(0, 2);
    if (BRAZIL_STATES[directCode]) {
      detectedState = BRAZIL_STATES[directCode];
    }
    return {
      countryCode: 'BR',
      countryName: 'Brazil',
      countryFlag: '🇧🇷',
      taxLabel: pure.length === 11 ? 'CPF' : 'CNPJ',
      expectedLength: 14,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: detectedState,
      formatted: pure,
    };
  }

  // 20. UNITED ARAB EMIRATES
  if (clean.startsWith('100') || upperRaw.startsWith('AE') || (outletCountryFallback === 'AE' && /^\d+$/.test(clean))) {
    const pure = clean.replace(/^AE/, '');
    const isComplete = pure.length === 15;
    const isValid = isComplete && /^\d{15}$/.test(pure);
    let detectedEmirate: string | undefined;
    const emiratePrefix = upperRaw.slice(0, 3);
    if (UAE_EMIRATES[emiratePrefix]) {
      detectedEmirate = UAE_EMIRATES[emiratePrefix];
    }
    return {
      countryCode: 'AE',
      countryName: 'United Arab Emirates',
      countryFlag: '🇦🇪',
      taxLabel: 'TRN',
      expectedLength: 15,
      currentLength: pure.length,
      isValid,
      isComplete,
      detectedRegion: detectedEmirate,
      formatted: pure,
    };
  }

  // 21. SAUDI ARABIA
  if (
    (clean.startsWith('3') && currentLength >= 2 && /^\d+$/.test(clean) && outletCountryFallback !== 'IN') ||
    upperRaw.startsWith('SA') ||
    (outletCountryFallback === 'SA' && /^\d+$/.test(clean))
  ) {
    const pure = clean.replace(/^SA/, '');
    const isComplete = pure.length === 15;
    const isValid = isComplete && /^\d{15}$/.test(pure);
    return {
      countryCode: 'SA',
      countryName: 'Saudi Arabia',
      countryFlag: '🇸🇦',
      taxLabel: 'VAT No.',
      expectedLength: 15,
      currentLength: pure.length,
      isValid,
      isComplete,
      formatted: pure,
    };
  }

  // 21A. ARGENTINA
  if (upperRaw.startsWith('AR') || upperRaw.startsWith('IIBB') || (/^(20|23|24|27|30|33|34)\d{9}$/.test(clean) && outletCountryFallback !== 'IN')) {
    let detectedProv: string | undefined;
    for (const [code, name] of Object.entries(ARGENTINA_PROVINCES)) {
      if (upperRaw.includes(code)) {
        detectedProv = name;
        break;
      }
    }
    const pure = clean.replace(/^AR/, '').replace(/^IIBB/, '');
    const isComplete = pure.length === 11;
    return {
      countryCode: 'AR',
      countryName: 'Argentina',
      countryFlag: '🇦🇷',
      taxLabel: detectedProv ? `IIBB (${detectedProv})` : 'CUIT',
      expectedLength: 11,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: detectedProv,
      formatted: pure.length === 11 ? `${pure.slice(0, 2)}-${pure.slice(2, 10)}-${pure.slice(10)}` : pure,
    };
  }

  // 21B. NIGERIA
  if (upperRaw.startsWith('NG') || (outletCountryFallback === 'NG' && /^\d+$/.test(clean))) {
    let detectedState: string | undefined;
    for (const [code, name] of Object.entries(NIGERIA_STATES)) {
      if (upperRaw.includes(`-${code}`) || upperRaw.startsWith(`NG-${code}`) || upperRaw.includes(code)) {
        detectedState = name;
        break;
      }
    }
    const pure = clean.replace(/^NG/, '');
    const isComplete = pure.length === 10 || pure.length === 12;
    return {
      countryCode: 'NG',
      countryName: 'Nigeria',
      countryFlag: '🇳🇬',
      taxLabel: detectedState ? `TIN (${detectedState})` : 'TIN (FIRS)',
      expectedLength: 10,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: detectedState,
      formatted: pure,
    };
  }

  // 21C. MEXICO
  if (upperRaw.startsWith('MX') || /^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/.test(clean) || (outletCountryFallback === 'MX' && currentLength >= 10)) {
    let detectedRegion: string | undefined;
    for (const [code, name] of Object.entries(MEXICO_REGIONS)) {
      if (upperRaw.includes(`-${code}`) || upperRaw.startsWith(`MX-${code}`)) {
        detectedRegion = name;
        break;
      }
    }
    const pure = clean.replace(/^MX/, '');
    const isComplete = pure.length === 12 || pure.length === 13;
    const isValid = /^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/.test(pure) || isComplete;
    return {
      countryCode: 'MX',
      countryName: 'Mexico',
      countryFlag: '🇲🇽',
      taxLabel: detectedRegion?.includes('8%') ? 'RFC (Border 8% Zone)' : 'RFC',
      expectedLength: 12,
      currentLength: pure.length,
      isValid,
      isComplete,
      detectedRegion,
      formatted: pure,
    };
  }

  // 21D. COLOMBIA
  if (upperRaw.startsWith('CO') || (outletCountryFallback === 'CO' && /^\d+$/.test(clean))) {
    let detectedDept: string | undefined;
    for (const [code, name] of Object.entries(COLOMBIA_DEPARTMENTS)) {
      if (upperRaw.includes(code)) {
        detectedDept = name;
        break;
      }
    }
    const pure = clean.replace(/^CO/, '');
    const isComplete = pure.length === 9 || pure.length === 10;
    return {
      countryCode: 'CO',
      countryName: 'Colombia',
      countryFlag: '🇨🇴',
      taxLabel: detectedDept ? `NIT (${detectedDept})` : 'NIT',
      expectedLength: 10,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: detectedDept,
      formatted: pure,
    };
  }

  // 21E. INDONESIA
  if (upperRaw.startsWith('ID') || (outletCountryFallback === 'ID' && /^\d+$/.test(clean))) {
    let detectedProv: string | undefined;
    for (const [code, name] of Object.entries(INDONESIA_PROVINCES)) {
      if (upperRaw.includes(code)) {
        detectedProv = name;
        break;
      }
    }
    const pure = clean.replace(/^ID/, '');
    const isComplete = pure.length === 15 || pure.length === 16;
    return {
      countryCode: 'ID',
      countryName: 'Indonesia',
      countryFlag: '🇮🇩',
      taxLabel: detectedProv ? `NPWP (${detectedProv})` : 'NPWP',
      expectedLength: 15,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      detectedRegion: detectedProv,
      formatted: pure,
    };
  }

  // 21F. ECUADOR
  if (upperRaw.startsWith('EC') || (outletCountryFallback === 'EC' && /^\d{13}$/.test(clean))) {
    const pure = clean.replace(/^EC/, '');
    const isComplete = pure.length === 13;
    const provCode = pure.slice(0, 2);
    const detectedProv = ECUADOR_PROVINCES[provCode];
    return {
      countryCode: 'EC',
      countryName: 'Ecuador',
      countryFlag: '🇪🇨',
      taxLabel: detectedProv ? `RUC (${detectedProv})` : 'RUC',
      expectedLength: 13,
      currentLength: pure.length,
      isValid: isComplete && /^\d{13}$/.test(pure),
      isComplete,
      detectedRegion: detectedProv,
      formatted: pure,
    };
  }

  // 21G. VIETNAM
  if (upperRaw.startsWith('VN') || (outletCountryFallback === 'VN' && /^\d{10}$/.test(clean))) {
    const pure = clean.replace(/^VN/, '');
    const isComplete = pure.length === 10;
    const deptCode = pure.slice(0, 2);
    const detectedDept = VIETNAM_PROVINCES[deptCode];
    return {
      countryCode: 'VN',
      countryName: 'Vietnam',
      countryFlag: '🇻🇳',
      taxLabel: detectedDept ? `MST (${detectedDept})` : 'Mã số thuế (MST)',
      expectedLength: 10,
      currentLength: pure.length,
      isValid: isComplete && /^\d{10}$/.test(pure),
      isComplete,
      detectedRegion: detectedDept,
      formatted: pure,
    };
  }

  // 21H. BOSNIA AND HERZEGOVINA
  if (upperRaw.startsWith('BA') || (outletCountryFallback === 'BA' && /^\d{13}$/.test(clean))) {
    let detectedEntity: string | undefined;
    for (const [code, name] of Object.entries(BOSNIA_ENTITIES)) {
      if (upperRaw.includes(code)) {
        detectedEntity = name;
        break;
      }
    }
    const pure = clean.replace(/^BA/, '');
    const isComplete = pure.length === 13;
    return {
      countryCode: 'BA',
      countryName: 'Bosnia and Herzegovina',
      countryFlag: '🇧🇦',
      taxLabel: detectedEntity ? `JIB (${detectedEntity})` : 'JIB / PDV',
      expectedLength: 13,
      currentLength: pure.length,
      isValid: isComplete && /^\d{13}$/.test(pure),
      isComplete,
      detectedRegion: detectedEntity,
      formatted: pure,
    };
  }

  // 21I. LATAM & CARIBBEAN FREE TRADE ZONES
  for (const [zoneKey, zoneLabel] of Object.entries(LATAM_FREE_ZONES)) {
    if (upperRaw.includes(zoneKey)) {
      const countryCode = zoneKey.startsWith('ZLC') || zoneKey.startsWith('PACIFICO') ? 'PA' : zoneKey === 'RZF' ? 'CR' : 'UY';
      const meta = GLOBAL_COUNTRIES[countryCode];
      return {
        countryCode,
        countryName: meta.name,
        countryFlag: meta.flag,
        taxLabel: `${meta.taxLabel} (0% Free Zone)`,
        expectedLength: meta.expectedLength || 10,
        currentLength,
        isValid: true,
        isComplete: true,
        detectedRegion: zoneLabel,
        formatted: upperRaw,
      };
    }
  }

  // 22. UNIVERSAL GLOBAL 2-LETTER ISO PREFIX DETECTOR
  const candidateIso = clean.slice(0, 2);
  if (GLOBAL_COUNTRIES[candidateIso] && candidateIso !== 'IN') {
    const meta = GLOBAL_COUNTRIES[candidateIso];
    const pure = clean.slice(2);
    const expected = meta.expectedLength || 10;
    const isComplete = pure.length >= (expected - 2);
    return {
      countryCode: candidateIso,
      countryName: meta.name,
      countryFlag: meta.flag,
      taxLabel: meta.taxLabel,
      expectedLength: expected,
      currentLength: pure.length,
      isValid: isComplete,
      isComplete,
      formatted: clean,
    };
  }

  // 23. OUTLET COUNTRY FALLBACK MATCH
  if (outletCountryFallback !== 'IN' && GLOBAL_COUNTRIES[outletCountryFallback]) {
    const meta = GLOBAL_COUNTRIES[outletCountryFallback];
    const expected = meta.expectedLength || 10;
    const isComplete = currentLength >= (expected - 2);
    return {
      countryCode: outletCountryFallback,
      countryName: meta.name,
      countryFlag: meta.flag,
      taxLabel: meta.taxLabel,
      expectedLength: expected,
      currentLength,
      isValid: isComplete,
      isComplete,
      formatted: clean,
    };
  }

  // 24. INDIA GSTIN (Default)
  const stateCode = clean.slice(0, 2);
  const detectedRegion = GST_STATE_CODES[stateCode];
  const isComplete = currentLength === 15;
  // Accepts any standard 15-character GSTIN structure without failing on the 15th checksum character
  const isValid = isComplete && GSTIN_REGEX.test(clean) && Boolean(detectedRegion);

  return {
    countryCode: 'IN',
    countryName: 'India',
    countryFlag: '🇮🇳',
    taxLabel: 'GSTIN',
    expectedLength: 15,
    currentLength,
    isValid,
    isComplete,
    detectedRegion,
    formatted: clean,
  };
}